import { decodeBase64, decodeBase64Url, encodeBase64Url, encodeBase64 } from './encode-base64';
import { AppSectionParamsDecodeError, IntegrationDataDecodeError, toError } from './errors';
export function extractIntegrationData(appSectionParams) {
  const {
    planIds,
    navigateToSectionProps,
    navigateToPageProps,
    biOptions,
    verticalStatusContent,
    restrictedPageId,
    restrictedPagePath,
    ...rest
  } = appSectionParams;
  const decodingErrors = [];
  function collectErrors(action) {
    return (input, fieldName) => {
      try {
        return action(input);
      } catch (error) {
        decodingErrors.push(fieldName + ': ' + input + ' ' + toError(error));
        return;
      }
    };
  }
  const safeAtob = collectErrors(atob);
  const safeDecode = collectErrors(decodeBase64);
  const integrationData = {
    ...rest,
    navigateToPageProps: navigateToPageProps && safeAtob(navigateToPageProps, 'navigateToPageProps'),
    navigateToSectionProps: navigateToSectionProps && safeDecode(navigateToSectionProps, 'navigateToSectionProps'),
    verticalStatusContent: verticalStatusContent && safeDecode(verticalStatusContent, 'verticalStatusContent'),
    biOptions: biOptions ? safeDecode(biOptions, 'biOptions') : undefined,
    planIds: planIds ? planIds.split(',') : undefined
  };
  return {
    integrationData,
    decodingError: decodingErrors.length > 0 ? new IntegrationDataDecodeError(decodingErrors.join(',')) : undefined
  };
}
export function extractRestrictedPageParams(appSectionParams) {
  if (appSectionParams.restrictedPageId || appSectionParams.restrictedPagePath) {
    return {
      restrictedPageId: appSectionParams.restrictedPageId,
      restrictedPagePath: appSectionParams.restrictedPagePath
    };
  }
  return undefined;
}
export function parseAppSectionParams(value) {
  try {
    return value ? JSON.parse(value) : {};
  } catch (e) {
    throw new AppSectionParamsDecodeError(toError(e).message);
  }
}
export function integrationDataFromQuery(query) {
  var _query$get, _query$get2, _query$get3, _query$get4, _query$get5, _query$get6, _query$get7, _query$get8, _query$get9;
  const navigateTo = (_query$get = query.get('destination')) != null ? _query$get : undefined;
  const navigateToPageProps = (_query$get2 = query.get('page')) != null ? _query$get2 : undefined;
  const section = (_query$get3 = query.get('section')) != null ? _query$get3 : undefined;
  const navigateToSectionProps = section ? decodeBase64Url(section) : undefined;
  const referralInfo = (_query$get4 = query.get('referral')) != null ? _query$get4 : undefined;
  const planIds = query.get('planIds');
  return {
    title: (_query$get5 = query.get('title')) != null ? _query$get5 : undefined,
    subtitle: (_query$get6 = query.get('subtitle')) != null ? _query$get6 : undefined,
    planIds: typeof planIds === 'string' ? planIds.split(',') : undefined,
    navigateTo,
    navigateToPageProps,
    navigateToSectionProps,
    verticalStatusContent: readVerticalStatusContent(query),
    biOptions: referralInfo ? {
      referralInfo,
      referralId: (_query$get7 = query.get('referralId')) != null ? _query$get7 : undefined
    } : undefined,
    minStartDate: (_query$get8 = query.get('minStartDate')) != null ? _query$get8 : undefined,
    maxStartDate: (_query$get9 = query.get('maxStartDate')) != null ? _query$get9 : undefined
  };
}
export function commonIntegrationDataToQuery(data) {
  const query = new URLSearchParams();
  data.navigateTo && query.set('destination', data.navigateTo);
  data.navigateToPageProps && query.set('page', data.navigateToPageProps);
  data.navigateToSectionProps && query.set('section', encodeBase64Url(data.navigateToSectionProps));
  if (data.biOptions) {
    const {
      referralInfo,
      referralId
    } = data.biOptions;
    referralInfo && query.set('referral', referralInfo);
    referralId && query.set('referralId', referralId);
  }
  data.maxStartDate && query.set('maxStartDate', data.maxStartDate);
  data.minStartDate && query.set('minStartDate', data.minStartDate);
  if (data.verticalStatusContent) {
    const {
      titleText,
      contentText,
      buttonText
    } = data.verticalStatusContent;
    titleText && query.set('thankYouTitle', titleText);
    contentText && query.set('thankYouMessage', contentText);
    buttonText && query.set('thankYouCTA', buttonText);
  }
  return query;
}
export function integrationDataToQuery(data) {
  const query = commonIntegrationDataToQuery(data);
  data.planIds && query.set('planIds', data.planIds.join(','));
  data.title && query.set('title', data.title);
  data.subtitle && query.set('subtitle', data.subtitle);
  return query;
}
function readVerticalStatusContent(query) {
  var _query$get10, _query$get11, _query$get12;
  const verticalTitleText = (_query$get10 = query.get('thankYouTitle')) != null ? _query$get10 : undefined;
  const verticalContentText = (_query$get11 = query.get('thankYouMessage')) != null ? _query$get11 : undefined;
  const verticalButtonText = (_query$get12 = query.get('thankYouCTA')) != null ? _query$get12 : undefined;
  return verticalTitleText || verticalContentText || verticalButtonText ? {
    titleText: verticalTitleText,
    contentText: verticalContentText,
    buttonText: verticalButtonText
  } : undefined;
}
export function integrationDataToPricingPageOptions(integrationData) {
  const {
    title,
    subtitle,
    planIds,
    maxStartDate,
    minStartDate,
    navigateToSectionProps,
    navigateTo,
    navigateToPageProps,
    biOptions,
    verticalStatusContent
  } = integrationData;
  const options = {};
  if (title) {
    options.title = title;
  }
  if (subtitle) {
    options.subtitle = subtitle;
  }
  if (planIds) {
    options.planIds = planIds;
  }
  if (biOptions) {
    options.biOptions = biOptions;
  }
  if (maxStartDate) {
    var _options$checkout;
    (_options$checkout = options.checkout) != null ? _options$checkout : options.checkout = {};
    options.checkout.maxStartDate = maxStartDate;
  }
  if (minStartDate) {
    var _options$checkout2;
    (_options$checkout2 = options.checkout) != null ? _options$checkout2 : options.checkout = {};
    options.checkout.minStartDate = minStartDate;
  }
  if (verticalStatusContent) {
    var _options$checkout3, _options$checkout4, _options$checkout4$th;
    (_options$checkout3 = options.checkout) != null ? _options$checkout3 : options.checkout = {};
    (_options$checkout4$th = (_options$checkout4 = options.checkout).thankYouPage) != null ? _options$checkout4$th : _options$checkout4.thankYouPage = {};
    options.checkout.thankYouPage.content = {
      title: verticalStatusContent == null ? void 0 : verticalStatusContent.titleText,
      message: verticalStatusContent == null ? void 0 : verticalStatusContent.contentText,
      cta: verticalStatusContent == null ? void 0 : verticalStatusContent.buttonText
    };
  }
  if (navigateToSectionProps) {
    var _options$checkout5, _options$checkout6, _options$checkout6$th;
    (_options$checkout5 = options.checkout) != null ? _options$checkout5 : options.checkout = {};
    (_options$checkout6$th = (_options$checkout6 = options.checkout).thankYouPage) != null ? _options$checkout6$th : _options$checkout6.thankYouPage = {};
    options.checkout.thankYouPage.navigation = {
      type: 'section',
      options: navigateToSectionProps
    };
  } else if (navigateTo) {
    var _options$checkout7, _options$checkout8, _options$checkout8$th;
    (_options$checkout7 = options.checkout) != null ? _options$checkout7 : options.checkout = {};
    (_options$checkout8$th = (_options$checkout8 = options.checkout).thankYouPage) != null ? _options$checkout8$th : _options$checkout8.thankYouPage = {};
    options.checkout.thankYouPage.navigation = {
      type: 'url',
      url: navigateTo
    };
  } else if (navigateToPageProps) {
    var _options$checkout9, _options$checkout10, _options$checkout10$t;
    (_options$checkout9 = options.checkout) != null ? _options$checkout9 : options.checkout = {};
    (_options$checkout10$t = (_options$checkout10 = options.checkout).thankYouPage) != null ? _options$checkout10$t : _options$checkout10.thankYouPage = {};
    options.checkout.thankYouPage.navigation = {
      type: 'page',
      pageId: navigateToPageProps
    };
  }
  return options;
}
export function integrationDataToAppSectionParams(integrationData) {
  const {
    planIds,
    navigateToPageProps,
    navigateToSectionProps,
    biOptions,
    verticalStatusContent,
    ...rest
  } = integrationData;
  return new URLSearchParams({
    appSectionParams: JSON.stringify({
      ...rest,
      planIds: planIds == null ? void 0 : planIds.join(','),
      biOptions: biOptions && encodeBase64(biOptions),
      navigateToSectionProps: navigateToSectionProps && encodeBase64(navigateToSectionProps),
      navigateToPageProps: navigateToPageProps && btoa(navigateToPageProps),
      verticalStatusContent: verticalStatusContent && encodeBase64(verticalStatusContent)
    })
  });
}
//# sourceMappingURL=integration-data.js.map